import type {
  MutableRefObject,
} from 'react';
import throttle from 'lodash/throttle';
import {
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';

export const useHorizontalScroll = (ref: MutableRefObject<HTMLDivElement>) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const handleScroll = useCallback((e?) => {
    const element = ref.current;
    const div = e?.target?.nodeName === 'DIV' ? e.target : element;
    const scrollLeft = div?.scrollLeft;
    const clientWidth = div?.clientWidth;
    const scrollWidth = div?.scrollWidth;
    const scrolled = scrollLeft > 0;
    setHasScrolled(scrolled);
    setIsOverflowing(clientWidth < scrollWidth);
    // consider end reached if less than 10px scroll remains
    setHasReachedEnd(scrolled && scrollWidth - clientWidth - scrollLeft < 10);
  }, []);

  const handleThrottle = throttle(handleScroll, 200);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element) {
      // Initiate calculations
      handleScroll();
      // Recalculate on scroll and resize
      element.addEventListener('scroll', handleThrottle);
      window.addEventListener('resize', handleThrottle);
    }
    return () => {
      window.removeEventListener('scroll', handleThrottle);
      window.addEventListener('resize', handleThrottle);
    };
  }, [ref.current]);

  return {
    isOverflowing,
    hasScrolled,
    hasReachedEnd,
  };
};
